import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Star, MessageSquare, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AMAService } from "@/services/amaService";
import { formatDistanceToNow } from "date-fns";
import { nb, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";

const AMAFeedCard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showInfoDialog, setShowInfoDialog] = useState(false);

  const { data: currentAMA } = useQuery({
    queryKey: ['current-ama'],
    queryFn: () => AMAService.getCurrentAMA(),
    refetchInterval: 60000,
  });

  if (!currentAMA) return null;

  const locale = i18n.language === 'no' ? nb : enUS;
  const isLive = currentAMA.status === 'live';
  const isPlanned = currentAMA.status === 'planned';
  const isFeatured = currentAMA.status === 'archived' && currentAMA.is_featured;


  const getButtonText = () => {
    if (isLive) return t('ama.banner.joinNow');
    if (isPlanned) return 'Still spørsmål her';
    return t('ama.banner.readMore');
  };

  return (
    <article 
      className={cn(
        "bg-white rounded-lg shadow-sm p-2 md:p-4 mb-6 transition-all duration-300 animate-fade-in hover:shadow-md relative cursor-pointer",
        "sticky top-0 z-10"
      )}
      onClick={() => navigate(`/ama/${currentAMA.id}`)}
      role="article"
      aria-label={`AMA: ${currentAMA.topic}`}
    >
      {/* Mobile layout */}
      <div className="flex md:hidden flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-shrink-0">
            <MessageSquare className="h-7 w-7 text-[#ea384c]" />
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[68%] text-[#ea384c] font-bold text-xs">
              ?
            </span>
          </div>

          <h3 className="font-bold text-sm text-[#ea384c] flex items-center gap-1">
            Dagens AMA
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowInfoDialog(true);
              }}
              className="inline-flex items-center"
              type="button"
            >
              <Info className="h-3.5 w-3.5 text-gray-500 hover:text-gray-700 transition-colors" />
            </button>
            kl. 18:00
          </h3>
        </div>

        <div className="flex items-center gap-2">
          {currentAMA.expert_photo_url && (
            <img
              src={currentAMA.expert_photo_url}
              alt={currentAMA.expert_name}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
            />
          )}
          
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-600 font-medium truncate">
              {currentAMA.expert_name}
            </p>
            {(currentAMA.expert_title || currentAMA.expert_company) && (
              <p className="text-[10px] text-gray-500 truncate">
                {[currentAMA.expert_title, currentAMA.expert_company].filter(Boolean).join(' • ')}
              </p>
            )}
          </div>

          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/ama/${currentAMA.id}`);
            }}
            className="bg-[#ea384c] hover:bg-[#ea384c]/90 text-white text-xs h-8 px-3 flex-shrink-0"
          >
            {getButtonText()}
          </Button>
        </div>
      </div>

      {/* Desktop layout */}
      <div className="hidden md:flex items-center gap-3">
        <div className="relative flex-shrink-0">
          <MessageSquare className="h-8 w-8 text-[#ea384c]" />
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[68%] text-[#ea384c] font-bold text-sm">
            ?
          </span>
        </div>

        <h3 className="font-bold text-base text-[#ea384c] flex items-center gap-1 flex-shrink-0">
          Dagens AMA
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setShowInfoDialog(true);
            }}
            className="inline-flex items-center"
            type="button"
          >
            <Info className="h-4 w-4 text-gray-500 hover:text-gray-700 transition-colors" />
          </button>
          kl. 18:00
        </h3>

        <p className="text-sm text-gray-600 font-semibold flex-shrink-0">
          {currentAMA.expert_name}
          {currentAMA.expert_title && ` • ${currentAMA.expert_title}`}
          {currentAMA.expert_company && ` • ${currentAMA.expert_company}`}
        </p>

        {currentAMA.expert_photo_url && (
          <img
            src={currentAMA.expert_photo_url}
            alt={currentAMA.expert_name}
            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
          />
        )}

        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/ama/${currentAMA.id}`);
          }}
          className="whitespace-nowrap flex-1 bg-[#ea384c] hover:bg-[#ea384c]/90 text-white"
        >
          {getButtonText()}
        </Button>
      </div>

      <Dialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Hva er AMA?</DialogTitle>
            <DialogDescription>
              AMA står for "Ask Me Anything" – et direkte-sendt intervju der du kan stille spørsmål til dagens ekspert. Send inn ditt spørsmål her. Følg med på svarene i sanntid når sendingen starter, eller les alle spørsmål og svar senere
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </article>
  );
};

export default AMAFeedCard;
